import type { ListFilterRule } from '@/layouts/02-Authenticated/layout/03-Content/routes/08-Relations/layout/03-Sections/views/02-Lists/routes/ListDetail/layout/03-FiltersRow/components/01-FilterDropdown';
import type { AuditLogExportFilter, AuditLogExportFilterField } from '@/services/database';
import type { AdvancedFilterDefinition } from '@/shared/components/filters';
import { evaluateJsonLogicRule, isJsonLogicRuleEmpty } from '@/shared/components/filters/jsonLogic';
import type { AuditLog } from '@/types';
import dayjs from '@/utilities/dayjs';

export interface AuditLogFilterRecord extends Pick<
    AuditLog,
    'action' | 'browser' | 'createdAt' | 'details' | 'deviceType' | 'entityId' | 'entityType' | 'ipAddress'
> {
    member?: Pick<AuditLog['member'], 'permission'>;
    user?: Pick<AuditLog['user'], 'emailAddress' | 'firstName' | 'lastName'>;
}

const auditLogFilterFields = new Set<AuditLogExportFilterField>([
    'action',
    'actor',
    'browser',
    'createdAt',
    'deviceType',
    'entityId',
    'ipAddress',
    'permission',
    'resource',
]);

const isAuditLogFilterField = (field: string): field is AuditLogExportFilterField =>
    auditLogFilterFields.has(field as AuditLogExportFilterField);

export const toAuditLogRequestFilters = (filters: ListFilterRule[]): AuditLogExportFilter[] =>
    filters.flatMap((filter) => {
        if (!isAuditLogFilterField(filter.field)) {return [];}

        return [
            {
                field: filter.field,
                operator: filter.operator,
                value: filter.value,
                ...(filter.valueJoin ? { valueJoin: filter.valueJoin } : {}),
            },
        ];
    });

export const toAuditLogRequestAdvancedFilter = (
    advancedFilter?: AdvancedFilterDefinition<ListFilterRule>,
): AdvancedFilterDefinition<AuditLogExportFilter> | undefined => {
    if (!advancedFilter) {return undefined;}

    return {
        groups: advancedFilter.groups.map((group) => ({
            conditions: group.conditions.flatMap((condition) => {
                const [rule] = toAuditLogRequestFilters([condition.rule]);

                return rule ? [{ ...condition, rule }] : [];
            }),
            id: group.id,
            join: group.join,
        })),
        schemaVersion: advancedFilter.schemaVersion,
    };
};

const acronyms: Record<string, string> = {
    api: 'API',
    oauth: 'OAuth',
    saml: 'SAML',
    sso: 'SSO',
};

export function titleize(value?: string | null) {
    if (!value) {return 'Unknown';}

    return String(value)
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[-_]/g, ' ')
        .trim()
        .split(/\s+/)
        .map((part) => acronyms[part.toLowerCase()] ?? `${part.charAt(0).toUpperCase()}${part.slice(1).toLowerCase()}`)
        .join(' ');
}

export function normalizeAuditAction(action?: string | null) {
    const value = String(action ?? '').trim();
    const normalized = value.replace(/[\s_-]/g, '').toLowerCase();

    const actionMap: Record<string, string> = {
        accessdenied: 'Access Denied',
        create: 'Create',
        delete: 'Delete',
        get: 'Read',
        partialupdate: 'Partial Update',
        patch: 'Partial Update',
        post: 'Create',
        put: 'Update',
        read: 'Read',
        update: 'Update',
    };

    return actionMap[normalized] ?? titleize(value);
}

export function normalizeAuditResource(resource?: string | null) {
    const value = String(resource ?? '').trim();
    const normalized = value.replace(/[\s_-]/g, '').toLowerCase();

    const resourceMap: Record<string, string> = {
        apikey: 'API Key',
        customfilter: 'Custom Filter',
        importedcompany: 'Imported Company',
        listview: 'List View',
        memocollaborator: 'Memo Collaborator',
        oauthapplication: 'OAuth Application',
        reportcomponent: 'Report Component',
    };

    return resourceMap[normalized] ?? titleize(value);
}

export function getAuditActorName(log: AuditLogFilterRecord) {
    const firstName = log.user?.firstName ?? '';
    const lastName = log.user?.lastName ?? '';
    const fullName = `${firstName} ${lastName}`.trim();

    return fullName || log.user?.emailAddress || 'Unknown User';
}

export function getAuditPermission(log: AuditLogFilterRecord) {
    return log.member?.permission ?? 'Unknown';
}

export function getAuditSearchText(log: AuditLogFilterRecord) {
    return [
        getAuditActorName(log),
        log.user?.emailAddress,
        getAuditPermission(log),
        normalizeAuditAction(log.action),
        normalizeAuditResource(log.entityType),
        log.browser,
        log.deviceType,
        log.ipAddress,
        log.entityId,
        JSON.stringify(log.details ?? {}),
    ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
}

export function getAuditLogJsonData(log: AuditLogFilterRecord) {
    return {
        action: normalizeAuditAction(log.action),
        actor: getAuditActorName(log),
        browser: log.browser ?? '',
        createdAt: dayjs(log.createdAt).format('YYYY-MM-DD'),
        deviceType: log.deviceType ?? '',
        entityId: log.entityId ?? '',
        ipAddress: log.ipAddress ?? '',
        permission: getAuditPermission(log),
        resource: normalizeAuditResource(log.entityType),
        search: getAuditSearchText(log),
    };
}

export function filterAuditLogs<TLog extends AuditLogFilterRecord>(
    logs: TLog[],
    query: string,
    filterExpression?: unknown,
) {
    const normalizedQuery = query.trim().toLowerCase();

    if (!normalizedQuery && isJsonLogicRuleEmpty(filterExpression)) {
        return logs;
    }

    return logs.filter((log) => {
        const filterData = getAuditLogJsonData(log);
        const matchesSearch = !normalizedQuery || String(filterData.search).includes(normalizedQuery);

        return matchesSearch && evaluateJsonLogicRule(filterExpression, filterData);
    });
}
